import { Grid } from '@material-ui/core';
import React from 'react';
import { useSelector } from 'react-redux';
import { styles } from '../top-customer-advert-styles';
import { IElectronicJournalRecord } from '../../common/interfaces/states/IElectronicJournalRecord';
import { EJRecordTypes } from '../../common/enumerations/ej-record-types';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { AppState } from 'src/common/redux/configure-store';
import { useTranslation } from 'react-i18next';
import {
  hasTaxItemFee,
  hasTaxTransactionFee,
} from 'src/action-window/business-logic/action-window-actions';

export enum ItemDetailType {
  Title = 'Title',
  Description = 'Description',
  Price = 'Price',
}

interface Props {
  item: IElectronicJournalRecord;
  type: ItemDetailType;
}

interface ItemDetails {
  Title?: JSX.Element | string;
  Description?: JSX.Element | string;
  Price?: JSX.Element | string;
}

const TopItemDetails: React.FC<Props> = ({ item, type }): JSX.Element => {
  const items = useSelector(
    (state: AppState): IElectronicJournalRecord[] => state.itemList.ejItems,
  );
  const classes = styles();
  const { t } = useTranslation();
  const getItemDetails = (): ItemDetails => {
    switch (item.Type) {
      case EJRecordTypes.Coupon:
        return {
          Title: t('Coupon'),
          Description: item.Name,
        };
      case EJRecordTypes.Promotion:
        return {
          // todo - we're assigning empty string to item's description somewhere, this is a band-aid for the time being
          Title: item.Description === '' ? item.Name : item.Description,
          Description: (
            <>
              {item.EligibilityInfo.map(item => (
                <Grid key={item.SequenceNumber} container className={classes.promotionRow}>
                  <Grid item xs={10}>
                    {item.Quantity} {item.Description}
                  </Grid>
                  <Grid
                    data-testid='top-customer-advert-savings'
                    item
                    xs={2}
                    className={classes.savings}
                  >
                    {item.NetPrice}
                  </Grid>
                </Grid>
              ))}
            </>
          ),
          Price: `-${convertCurrency(item.RawPrice || 0)}`,
        };
      case EJRecordTypes.Product:
        return {
          Title: `${item.Name}`,
          Price: convertCurrency(item.RawPrice || 0),
          Description: (
            <>
              {item.Taxes &&
                item.Taxes.map(tax => {
                  const amount =
                    item.Taxes?.reduce((total, { amount }) => total + (amount ?? 0), 0) ?? 0;
                  if (hasTaxItemFee(tax) || hasTaxTransactionFee(tax)) {
                    return (
                      <Grid key={item.SequenceNumber} container className={classes.itemFeeRow}>
                        <Grid item xs={10} data-testid={`top-item-details-tax-fee-name`}>
                          {tax.code}
                        </Grid>
                        <Grid
                          data-testid={`top-item-tax-fee-amount`}
                          item
                          xs={2}
                          className={classes.alignRight}
                        >
                          {`+${convertCurrency(amount)}`}
                        </Grid>
                      </Grid>
                    );
                  } else {
                    return null;
                  }
                })}
            </>
          ),
        };
      case EJRecordTypes.ItemFee:
        return {
          Title: t('Item_Fee_Total'),
          Price: convertCurrency(+item.Price),
        };
      case EJRecordTypes.TransactionFee:
        return {
          Title: t('Transaction_Fee_Total'),
          Price: convertCurrency(+item.Price),
        };
      case EJRecordTypes.TaxExemption:
        return {
          Title: item.Name,
          Price: convertCurrency(item.RawPrice || 0),
          Description: (
            <>
              {items
                ?.filter(({ Taxes }) => Taxes?.some(tax => tax.exemptAmount))
                .map((item, index) => {
                  const taxExempted =
                    item.Taxes?.reduce(
                      (total, { exemptAmount }) => total + (exemptAmount ?? 0),
                      0,
                    ) ?? 0;
                  return (
                    <Grid key={item.SequenceNumber} container className={classes.taxExemptionRow}>
                      <Grid
                        item
                        xs={10}
                        data-testid={`top-item-details-tax-exemption-description-${index}`}
                      >
                        {item.Name}
                      </Grid>
                      <Grid
                        data-testid={`top-item-details-tax-exemption-amount-${index}`}
                        item
                        xs={2}
                        className={classes.alignRight}
                      >
                        {`${convertCurrency(-taxExempted)}`}
                      </Grid>
                    </Grid>
                  );
                })}
            </>
          ),
        };
      default:
        return {};
    }
  };

  return <>{getItemDetails()[type]}</>;
};

export default TopItemDetails;
